import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Tabs, Typography, Button, Tooltip, Row, Col, Empty, message} from 'antd';
import { ArrowLeftOutlined, EyeOutlined, EyeInvisibleOutlined, CalculatorOutlined } from '@ant-design/icons';
import ScenarioTable from '../../components/ScenarioTable';
import OverlayedLoader from '../../components/OverlayedLoader';
import Localization from '../../utils/localization';
import {
    accountActions,
    allocationActions,
    entityActions,
    organizationActions,
    businessActions,
    periodsActions,
} from '../../redux/actions';

import {
  selectActiveResult,
  selectShowPreview,
  selectCurrentKey,
  selectBucketByKey,
} from '../../redux/slices/assemblySlice';

import {
  assembleBaseline,
  assemblePreview,
} from '../../redux/actions/assemblyActions';

import { useColumnWidths } from '../../hooks/useColumnWidths';

import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';

const { Content } = Layout;
const { TabPane } = Tabs;
const { Title } = Typography;
const DEFAULT_WIDTHS = { account_number: 90, name: 150 };

const BusinessCase = () => {
    const [columnWidths, setColumnWidth] = useColumnWidths(
      'scenarioTable.columnWidths',  // ← same key on all three pages
      DEFAULT_WIDTHS
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const urlParams = useParams();
    const fetchingEntities = useSelector((state) => state.entities.fetchingEntities);
    const fetchingStructure = useSelector((state) => state.accounts.fetchingStructure);
    const accountStructure = useSelector((state) => state.accounts.accountStructure);
    const calculationAccountStructure = useMemo(
      () =>
        buildCalculationAccountStructure(
          accountStructure,
        ),
      [accountStructure],
    );
    
    const periods = useSelector((state) => state.periods.periods);
    const businessCases = useSelector((state) => state.business.businessCases);
    const assemblingBusinessCase = useSelector((state) => state.business.assemblingCase);
    const assembledBusinessCase = useSelector((state) => state.business.businessCase);
    
    const active = useSelector(selectActiveResult);       
    const showPreview = useSelector(selectShowPreview);

    const {
    fetchingPeriods,
    periods: periodsArr,
    fetchingExpandedPeriods,
    expandedPeriods: expandedFlags,
    } = useSelector((state) => state.periods || {});

    const periodsReady  = Array.isArray(periodsArr) && periodsArr.length > 0 && !fetchingPeriods;
    const expandedReady = Array.isArray(expandedFlags) && !fetchingExpandedPeriods;

    // You'll also want allocations if you show BC as “current plan” with allocations applied:
    const allocations = useSelector((state) => state.allocations.allocations);

    

    useEffect(() => {
        dispatch(entityActions.getEntities());
        dispatch(accountActions.getAccountStructure());
        dispatch(allocationActions.getAllocations());
        dispatch(organizationActions.getOrganization());
        dispatch(businessActions.getBusinessCases());
        dispatch(periodsActions.getPeriods());
    }, [dispatch]);
    
    const bcId = Number(urlParams.key);
    const bc = businessCases?.find(b => b.business_case_id === bcId);
    const bcAllocations = (allocations || []).filter(a => a.businessCaseId === bcId);
    const hasBcAllocations = bcAllocations.length > 0;

    useEffect(() => {
        // prerequisites loaded?
       if (!calculationAccountStructure || !businessCases || businessCases.length === 0) return;
       if (!periodsReady || !expandedReady) return;
       if (!bc) return;
       
       console.log('[BusinessCase] dispatch assembleBaseline', { bcId: bc.business_case_id });
        
      // Assemble BASELINE via the new thunk (cached in assembly slice)
      dispatch(assembleBaseline({
          bc,
          periods: periodsArr,
          accountStructure: calculationAccountStructure,
          expandedPeriods: expandedFlags,
          allocations:[],
      }));

        
        }, [
        calculationAccountStructure, businessCases, periodsReady, expandedReady,
        urlParams.key, dispatch, periodsArr, expandedFlags, bc
        ]);

    const scenarioForTables =
    active?.assembledBusinessCase || assembledBusinessCase || null; // prefer assembly slice

    const assemblyCompiling = useSelector(s => s.assembly.assemblyCompiling); 
    const disabled = !bc || assemblyCompiling;
    const hasAllocForSelected = hasBcAllocations; // reuse your existing computed 
    const handleReassembleBC = async () => {
      if (!bc) return;

      const args = {
        bc,
        periods: periodsArr,
        accountStructure: calculationAccountStructure,
        expandedPeriods: expandedFlags,
        allocations: showPreview ? bcAllocations : [],
        force: true,
      };

      if (showPreview) {
        if (!hasBcAllocations) {
          message.warning('No allocations available to use.');
          return;
        }
        await dispatch(assemblePreview(args));
      } else {
        await dispatch(assembleBaseline(args));
      }
    };
   
    const handleClick = async () => {
      if (!bc || !calculationAccountStructure) return;
      const next = !showPreview;
      
      if (next) {
        if (!hasBcAllocations) {
          message.warning('No allocations available to use.');
          return;
        }
        
        await dispatch(assemblePreview({
          bc,
          periods: periodsArr,
          accountStructure: calculationAccountStructure,
          expandedPeriods: expandedFlags,
          allocations: bcAllocations,
        }));
      } else {
        await dispatch(assembleBaseline({
          bc,
          periods: periodsArr,
          accountStructure: calculationAccountStructure,
          expandedPeriods: expandedFlags,
          allocations: [],
        }));
      }

    };

    const renderContent = () => {
      if (fetchingEntities || fetchingStructure || assemblingBusinessCase) {
        return <OverlayedLoader />;
      }

  if (!scenarioForTables) {
    return (
      <Empty
        description="No business case to display yet"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      >
        <Button type="primary" onClick={() => navigate('/business-cases')}>
          Choose a Business Case
        </Button>
      </Empty>
    );
  }

  return (
    <div>
      {/* Header (replaces your <Title> ... </Title> block) */}
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
  <Button
    type="text"
    onClick={() => navigate('/business-cases')}
    aria-label="Back to Business Cases"
  >
    <ArrowLeftOutlined style={{ fontSize: '150%' }} />
  </Button>

  <Title level={2} style={{ margin: 0 }}>
    {active?.assembledBusinessCase?.name ||
      assembledBusinessCase?.name ||
      'Business Case'}
  </Title>
  <span style={{ padding: "2px 6px", background: "yellow" }}>HEADER RENDERED</span>

  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
  {showPreview && !hasAllocForSelected ? (
    <Tooltip title="No allocations available to use">
      <span>
        <Button
          shape="circle"
          onClick={handleClick}
          disabled={disabled}
          loading={assemblyCompiling}
          aria-pressed={showPreview}
          icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        />
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={showPreview ? 'With BC allocations' : 'Without BC allocations'}>
      {disabled ? (
        <span>
          <Button
            shape="circle"
            onClick={handleClick}
            disabled={disabled}
            loading={assemblyCompiling}
            aria-pressed={showPreview}
            icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          />
        </span>
      ) : (
        <Button
          shape="circle"
          onClick={handleClick}
          disabled={disabled}
          loading={assemblyCompiling}
          aria-pressed={showPreview}
          icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        />
      )}
    </Tooltip>
  )}
  <Tooltip title="Run business case (force)">
  <span>
    <Button
       type="primary"
      icon={<CalculatorOutlined />}
      onClick={handleReassembleBC}
      disabled={!bc || assemblyCompiling}
      loading={assemblyCompiling}
    />
  </span>
</Tooltip>
</div>
</div>
      <Tabs type="card" destroyInactiveTabPane>
        <TabPane tab={Localization.common.profitAndLoss} key="1">
          <Title>{Localization.common.profitAndLoss}</Title>
          <ScenarioTable
            accounts={accountStructure?.profitloss}
            structure={accountStructure}
            scenario={scenarioForTables}
            periods={periodsArr} 
            handleChange={(record) => console.log('Saved:', record)}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
          />
        </TabPane>

        <TabPane tab={Localization.common.balanceSheet} key="2">
          <Title level={2} style={{ textAlign: 'left' }}>
            {Localization.common.balanceSheet}
          </Title>
          <Title level={3} style={{ textAlign: 'left' }}>
            {Localization.common.assets}
          </Title>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.assets}
            structure={accountStructure}
            scenario={scenarioForTables}
            periods={periodsArr}
            handleChange={(record) => console.log('Saved:', record)}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
          />

          <Title level={2}>{Localization.common.liabilities}</Title>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.liabilities}
            structure={accountStructure}
            scenario={scenarioForTables}
            periods={periodsArr}
            handleChange={(record) => console.log('Saved:', record)}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
          />

          <Title level={2}>{Localization.common.equities}</Title>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.equities}
            structure={accountStructure}
            scenario={scenarioForTables}
            periods={periodsArr}
            handleChange={(record) => console.log('Saved:', record)}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
          />
        </TabPane>

        <TabPane tab={Localization.common.cashFlow} key="3">
          <Title>{Localization.common.cashFlow}</Title>
          <ScenarioTable
            accounts={accountStructure?.cashflow}
            structure={accountStructure}
            scenario={scenarioForTables}
            periods={periodsArr}
            handleChange={(record) => console.log('Saved:', record)}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};
return (
  <Layout style={{ height: '100vh', marginLeft: '-16px' }}>
    <Content>{renderContent()}</Content>
  </Layout>
);
}

export default BusinessCase;

